import { Button, Card, CardHeader, Badge, EmptyState, Table, useToast } from "@helmr/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { addHoliday, createBranch, createBrand, deleteBranch, deleteBrand, getBranch, listBranches, listBrands, patchBranch, patchBrand, removeHoliday,
  type Address, type Branch, type Brand } from "../../api/settings";
import { BranchHoursEditor } from "./editors";
import { ConfirmDrawer, Field, FormSkeleton, Load, mapError, Page, TableSkeleton, useScope, type Mapped } from "./kit";
import { SectionForm, type CustomProps, type FieldDef } from "./SectionForm";
import s from "./settings.module.css";
import { section } from "./sections";
import { t } from "./strings";
import { Drawer } from "@helmr/ui";

const ADDRESS_KEYS = [["line1", "Address line 1"], ["line2", "Address line 2"], ["city", "City"], ["state", "State"], ["postalCode", "Postal code"], ["country", "Country (2 letters)"]] as const;

function AddressEditor({ value, onChange, disabled }: CustomProps) {
  const a = (value as Address | null) ?? {};
  const set = (k: string, v: string) => {
    const next = { ...a, [k]: v };
    const clean = Object.fromEntries(Object.entries(next).filter(([, x]) => (x ?? "").trim() !== ""));
    onChange(Object.keys(clean).length ? clean : null);
  };
  return (
    <div className={s.stack}>
      {ADDRESS_KEYS.map(([k, label]) => (
        <label key={k} className={s.field}><span className={s.lab}>{label}</span>
          <input className={s.input} disabled={disabled} value={a[k] ?? ""} onChange={(e) => set(k, k === "country" ? e.target.value.toUpperCase() : e.target.value)} aria-label={label} /></label>
      ))}
    </div>
  );
}

const branchLine = (b: Branch) => [b.address?.line1, b.address?.city].filter(Boolean).join(", ");

/* ---------- list: brands then branches ---------- */

export function BranchesPage() {
  const sc = useScope();
  const brands = useQuery({ queryKey: sc.key("brands"), queryFn: () => listBrands(sc.client), retry: false });
  const branches = useQuery({ queryKey: sc.key("branches"), queryFn: () => listBranches(sc.client), retry: false });
  const canEdit = sc.ctx.has("settings.brands_branches_edit");
  const [brandForm, setBrandForm] = useState<Brand | "new" | null>(null);
  const [delBrand, setDelBrand] = useState<Brand | null>(null);
  const brandName = (id: string) => brands.data?.find((b) => b.id === id)?.name ?? "";
  return (
    <Page title={t.sections.branches.title} description={t.sections.branches.body} allow={section("branches")}>
      <Card aria-label="Brands">
        <CardHeader title="Brands" subtitle="A brand groups branches under one name and WhatsApp number." action={canEdit ? <Button size="sm" onClick={() => setBrandForm("new")}>Add brand</Button> : null} />
        <Load query={brands} what="brands" skeleton={<TableSkeleton rows={2} />}>
          {(list) => list.length === 0
            ? <EmptyState compact icon="badge" title="No brands yet" description="Add the first brand to start adding branches." action={canEdit ? <Button onClick={() => setBrandForm("new")}>Add brand</Button> : undefined} />
            : <Table label="Brands" rows={list} rowKey={(b) => b.id}
              columns={[
                { key: "n", header: "Brand", cell: (b) => <span className={s.name} title={b.name}>{b.name}</span> },
                { key: "w", header: "WhatsApp number", cell: (b) => b.whatsappNumber ?? "—" },
                { key: "a", header: "Actions", srOnly: true, align: "end", cell: (b) => canEdit ? <span className={s.rowActions}><Button size="sm" onClick={() => setBrandForm(b)} aria-label={`Edit ${b.name}`}>Edit</Button><Button size="sm" onClick={() => setDelBrand(b)} aria-label={`Delete ${b.name}`}>Delete</Button></span> : null },
              ]} />}
        </Load>
      </Card>

      <Card aria-label="Branches">
        <CardHeader title="Branches" action={canEdit ? <Button size="sm" variant="primary" as={Link} to="/settings/branches/new">Add branch</Button> : null} />
        <Load query={branches} what="branches" skeleton={<TableSkeleton />}>
          {(list) => list.length === 0
            ? <EmptyState compact icon="badge" title="No branches yet" description="Add the first branch." action={canEdit ? <Button as={Link} to="/settings/branches/new">Add branch</Button> : undefined} />
            : <Table label="Branches" rows={list} rowKey={(b) => b.id} minWidth={560}
              columns={[
                { key: "n", header: "Branch", cell: (b) => <Link to={`/settings/branches/${b.id}`}><span className={s.name} title={b.name}>{b.name}</span><span className={s.sub} title={branchLine(b)}>{branchLine(b) || "No address yet"}</span></Link> },
                { key: "b", header: "Brand", cell: (b) => brandName(b.brandId) },
                { key: "t", header: "Timezone", cell: (b) => b.effective.timezone },
                { key: "g", header: "Tax ID", cell: (b) => b.effective.gstin ?? <Badge tone="neutral">None</Badge> },
              ]} />}
        </Load>
      </Card>

      <BrandDrawer value={brandForm} onClose={() => setBrandForm(null)} />
      <DeleteDrawer kind="brand" name={delBrand?.name ?? ""} open={delBrand !== null} onClose={() => setDelBrand(null)}
        run={() => deleteBrand(sc.client, delBrand!.id)} invalidate={["brands", "branches"]} />
    </Page>
  );
}

function BrandDrawer({ value, onClose }: { value: Brand | "new" | null; onClose: () => void }) {
  const sc = useScope();
  const toast = useToast();
  const editing = value !== null && value !== "new" ? value : null;
  const [form, setForm] = useState({ name: "", logoUrl: "", whatsappNumber: "" });
  const [err, setErr] = useState<Mapped>({ fields: {}, form: null });
  const [seen, setSeen] = useState<typeof value>(null);
  if (seen !== value) { setSeen(value); setForm({ name: editing?.name ?? "", logoUrl: editing?.logoUrl ?? "", whatsappNumber: editing?.whatsappNumber ?? "" }); setErr({ fields: {}, form: null }); }
  const m = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { name: form.name.trim() };
      if (form.logoUrl.trim() || editing?.logoUrl) body.logoUrl = form.logoUrl.trim() || null;
      if (form.whatsappNumber.trim() || editing?.whatsappNumber) body.whatsappNumber = form.whatsappNumber.trim() || null;
      return editing ? patchBrand(sc.client, editing.id, body) : createBrand(sc.client, body);
    },
    onSuccess: async () => { await sc.qc.invalidateQueries({ queryKey: sc.key("brands") }); toast.show(t.saved); onClose(); },
    onError: (e) => setErr(mapError(e, ["name", "logoUrl", "whatsappNumber"])),
  });
  const f = (k: keyof typeof form) => ({ value: form[k], onChange: (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value }) });
  return (
    <Drawer open={value !== null} onOpenChange={(o) => { if (!o) onClose(); }} title={editing ? `Edit ${editing.name}` : "Add brand"}
      footer={<><Button onClick={onClose}>{t.cancel}</Button><Button variant="primary" busy={m.isPending} onClick={() => m.mutate()}>{editing ? t.save : "Add brand"}</Button></>}>
      <div className={s.stack}>
        <Field label="Name" error={err.fields.name} htmlFor="brand-name"><input id="brand-name" className={s.input} {...f("name")} aria-invalid={!!err.fields.name} /></Field>
        <Field label="Logo URL" error={err.fields.logoUrl} hint="An https link." htmlFor="brand-logo"><input id="brand-logo" className={s.input} {...f("logoUrl")} /></Field>
        <Field label="WhatsApp number" error={err.fields.whatsappNumber} htmlFor="brand-wa"><input id="brand-wa" className={s.input} {...f("whatsappNumber")} /></Field>
        {err.form ? <div className={s.formErr} role="alert" style={{ margin: 0 }}>{err.form}</div> : null}
      </div>
    </Drawer>
  );
}

/** Delete a brand or branch: the name must be typed, and the API's refusal is shown as it is sent. */
function DeleteDrawer({ kind, name, open, onClose, run, invalidate, onDone }: {
  kind: "brand" | "branch"; name: string; open: boolean; onClose: () => void; run: () => Promise<unknown>; invalidate: string[]; onDone?: () => void;
}) {
  const sc = useScope();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: run,
    onSuccess: async () => { await Promise.all(invalidate.map((k) => sc.qc.invalidateQueries({ queryKey: sc.key(k) }))); toast.show(`Deleted ${name}`); setError(null); onClose(); onDone?.(); },
    onError: (e) => setError(mapError(e, []).form),
  });
  return (
    <ConfirmDrawer open={open} onClose={() => { setError(null); onClose(); }} title={`Delete ${kind}`} description={`This removes ${name} from the gym. History stays in the audit log.`}
      confirmLabel={`Delete ${kind}`} busyLabel="Deleting…" busy={m.isPending} error={error} typeToConfirm={name} onConfirm={() => m.mutate()} />
  );
}

/* ---------- one branch ---------- */

export function BranchPage() {
  const { id = "" } = useParams();
  const isNew = id === "new";
  const sc = useScope();
  const nav = useNavigate();
  const brands = useQuery({ queryKey: sc.key("brands"), queryFn: () => listBrands(sc.client), retry: false });
  const [del, setDel] = useState(false);
  const canEdit = sc.ctx.has("settings.brands_branches_edit");
  const live = (brands.data ?? []).filter((b) => b.archivedAt === null);
  const fields: FieldDef[] = [
    { key: "name", path: ["name"], label: "Branch name", kind: "text", full: true },
    ...(isNew ? [{ key: "brandId", path: ["brandId"], label: "Brand", kind: "select", options: live.map((b) => ({ value: b.id, label: b.name })), hint: "Optional when the gym has one brand." } satisfies FieldDef] : []),
    { key: "address", path: ["address"], label: "Address", kind: "custom", full: true, render: (p) => <AddressEditor {...p} /> },
    { key: "timezone", path: ["timezone"], label: "Timezone", kind: "text", nullable: true, hint: "Empty uses the business timezone." },
    { key: "gstin", path: ["gstin"], label: "Tax ID (GSTIN)", kind: "text", nullable: true, upper: true, hint: "Empty uses the business tax ID." },
    { key: "stateCode", path: ["stateCode"], label: "State code", kind: "text", nullable: true, upper: true },
    { key: "paymentAccountRef", path: ["paymentAccountRef"], label: "Payment account reference", kind: "text", nullable: true },
    { key: "hours", path: ["hours"], label: "Opening hours", kind: "custom", full: true, render: (p) => <BranchHoursEditor {...p} /> },
  ];
  const blank: Branch = { id: "", brandId: "", name: "", address: null, timezone: null, hours: null, gstin: null, stateCode: null, paymentAccountRef: null, effective: { timezone: "", gstin: null, paymentAccountRef: null }, createdAt: 0, updatedAt: 0, archivedAt: null };
  // Same key and fetcher as the form below, so the header and the delete confirm read the loaded branch from the one cache entry.
  const loaded = useQuery({ queryKey: sc.key(`branch:${id}`), queryFn: () => getBranch(sc.client, id), enabled: !isNew, retry: false });
  const name = isNew ? "New branch" : (loaded.data?.name ?? "Branch");
  return (
    <Page title={isNew ? "Add branch" : name} description={t.sections.branches.body} allow={section("branches")}
      actions={!isNew && canEdit ? <Button onClick={() => setDel(true)}>Delete branch</Button> : undefined}>
      <SectionForm key={id} title={isNew ? "New branch" : "Branch details"} what="the branch" queryKey={`branch:${id}`} blankForm={isNew}
        load={async (c) => (isNew ? blank : getBranch(c, id))}
        save={(c, patch) => (isNew ? createBranch(c, patch) : patchBranch(c, id, patch).then((b) => b))}
        fields={fields} canEdit={canEdit}
        onSaved={() => { void sc.qc.invalidateQueries({ queryKey: sc.key("branches") }); nav("/settings/branches"); }} />
      {!isNew ? <DeleteDrawer kind="branch" name={loaded.data?.name ?? ""} open={del} onClose={() => setDel(false)}
        run={() => deleteBranch(sc.client, id)} invalidate={["branches"]} onDone={() => nav("/settings/branches")} /> : null}
    </Page>
  );
}

/* ---------- holidays ---------- */

export function HolidaysPage() {
  const sc = useScope();
  const toast = useToast();
  const branches = useQuery({ queryKey: sc.key("branches"), queryFn: () => listBranches(sc.client), retry: false });
  const [branchId, setBranchId] = useState("");
  const [date, setDate] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState<Mapped>({ fields: {}, form: null });
  const canEdit = sc.ctx.has("staff_attendance.holidays");
  const effectiveBranch = branchId || branches.data?.[0]?.id || "";
  const add = useMutation({
    mutationFn: () => addHoliday(sc.client, { branchId: effectiveBranch, date, name: name.trim() }),
    onSuccess: () => { toast.show(`Holiday set for ${date}`); setErr({ fields: {}, form: null }); setName(""); },
    onError: (e) => setErr(mapError(e, ["name", "date", "branchId"])),
  });
  const remove = useMutation({
    mutationFn: () => removeHoliday(sc.client, { branchId: effectiveBranch, date }),
    onSuccess: () => { toast.show(`Holiday removed for ${date}`); setErr({ fields: {}, form: null }); },
    onError: (e) => setErr(mapError(e, ["date", "branchId"])),
  });
  return (
    <Page title={t.sections.holidays.title} description={t.sections.holidays.body} allow={section("holidays")}>
      <Card aria-label="Holidays">
        <CardHeader title="Set or remove a holiday" subtitle="Setting a date again renames it. The server has no list of holidays yet, so removal is by date." />
        <Load query={branches} what="branches" skeleton={<FormSkeleton rows={3} />}>
          {(list) => (
            <div className={s.fields}>
              <Field label="Branch" htmlFor="h-branch" error={err.fields.branchId}>
                <select id="h-branch" className={s.select} value={effectiveBranch} onChange={(e) => setBranchId(e.target.value)}>{list.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</select>
              </Field>
              <Field label="Date" htmlFor="h-date" error={err.fields.date}><input id="h-date" type="date" className={s.input} value={date} onChange={(e) => setDate(e.target.value)} /></Field>
              <Field label="Name" htmlFor="h-name" error={err.fields.name} full><input id="h-name" className={s.input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Diwali" /></Field>
              {err.form ? <div className={`${s.formErr} ${s.full}`} role="alert" style={{ margin: 0 }}>{err.form}</div> : null}
              {canEdit ? (
                <div className={`${s.acts} ${s.full}`}>
                  <Button variant="primary" busy={add.isPending} busyLabel="Saving…" disabled={!date || !name.trim() || !effectiveBranch} onClick={() => add.mutate()}>Set holiday</Button>
                  <Button busy={remove.isPending} busyLabel="Removing…" disabled={!date || !effectiveBranch} onClick={() => remove.mutate()}>Remove holiday on this date</Button>
                </div>
              ) : <p className={s.hint}>You can view this, not change it.</p>}
            </div>
          )}
        </Load>
      </Card>
    </Page>
  );
}
